import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authAPI } from '../../api/auth';
import { mfaAPI } from '../../api/mfa';
import { TokenService } from '../../auth/services/tokenService';
import DarkModeToggle from '../DarkModeToggle';
import styles from './LoginForm.module.css';

import { logger } from '../../utils/logger';
import { lastFatalSummary, dumpBlackBox } from '../../utils/authBlackBox';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState<{ email?: string; password?: string }>({});
  // Second-factor step. `mfaRequired` flips once the backend has told us this
  // account has MFA on; from then on the code field is shown and submitted with
  // the (unchanged) credentials. See docs/AUTH_FIXES_LEDGER.md 2026-08-24 for
  // why verification is single-step rather than a challenge-token exchange.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  // Auth black-box: why did the previous session end? Logged to the CONSOLE
  // only (no on-screen UI — this is production). Support/devs read it via
  // DevTools or `window.authBlackBox()`.
  useEffect(() => {
    // A stale invitation token from an abandoned flow would silently redirect
    // the next Google sign-in to the invitation endpoint.
    sessionStorage.removeItem('invitation_token');

    const summary = lastFatalSummary();
    if (summary) {
      // eslint-disable-next-line no-console
      console.info(
        '🔎 Last session ended:', summary,
        '— full trail: run window.authBlackBox() in this console',
      );
      // eslint-disable-next-line no-console
      console.debug('auth black-box dump:', dumpBlackBox());
    }
  }, []);

  const { login, isLoading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const verificationMessage = location.state?.verified
    ? 'Email verified. Please sign in to continue.'
    : null;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/auth/continue', { replace: true, state: { from: location.state?.from } });
    }
  }, [isAuthenticated, navigate, location]);

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(email, password, rememberMe, mfaRequired ? mfaCode : undefined);
      // Redirect will happen automatically via useEffect when isAuthenticated changes
    } catch (err) {
      // A code-less login against an MFA-enabled account comes back as a 401,
      // the same status as a wrong password. Without this branch the user is
      // told their perfectly correct credentials are invalid.
      if (mfaAPI.isMfaRequiredError(err)) {
        setMfaRequired(true);
        setMfaCode('');
      }
      // Everything else is surfaced by the auth context.
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);

      const { url } = await authAPI.getGoogleAuthUrl();
      sessionStorage.removeItem('auth_google_return');
      if (typeof location.state?.from?.pathname === 'string') {
        const { pathname, search, hash } = location.state.from;
        sessionStorage.setItem('auth_google_return', JSON.stringify({ pathname, search, hash }));
      }

      // Carry the "Remember me" choice across the Google redirect so the
      // callback can request the extended (30-day) refresh-token lifetime.
      // sessionStorage survives the round-trip to Google within the same tab.
      sessionStorage.setItem('google_oauth_remember_me', rememberMe.toString());

      // Clear any stale tokens before redirecting to Google. The callback
      // exchanges the code for fresh credentials and then explicitly asks
      // AuthContext to re-initialize. Tagged 'oauth_scrub' so the black-box
      // records this intentional wipe as benign, not a session-ending kick.
      TokenService.clearTokens('oauth_scrub');

      // Redirect to Google
      window.location.href = url;
    } catch (err) {
      logger.error('Failed to start Google login:', err);
      setGoogleLoading(false);
    }
  };

  // Don't render if already authenticated (will redirect)
  if (isAuthenticated) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <div className={styles.header}>
            <h1 className={styles.title}>Redirecting...</h1>
            <p className={styles.subtitle}>You are already logged in</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.themeToggle}>
        <DarkModeToggle />
      </div>
      <div className={styles.formWrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Welcome Back</h1>
          <p className={styles.subtitle}>Sign in to your LazySEM account</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${styles.input} ${formErrors.email ? styles.error : ''}`}
              placeholder="Enter your email"
              disabled={isLoading}
              autoComplete="email"
            />
            {formErrors.email && (
              <span className={styles.errorMessage}>{formErrors.email}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${styles.input} ${formErrors.password ? styles.error : ''}`}
                placeholder="Enter your password"
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {formErrors.password && (
              <span className={styles.errorMessage}>{formErrors.password}</span>
            )}
          </div>

          <div className={styles.options}>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
              />
              <span className={styles.checkmark}></span>
              Remember me
            </label>
            <Link to="/forgot-password" className={styles.forgotPassword}>
              Forgot password?
            </Link>
          </div>

          {mfaRequired && (
            <div className={styles.field}>
              <label htmlFor="mfaCode" className={styles.label}>
                Authentication code
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className={styles.input}
                placeholder="6-digit code"
                disabled={isLoading}
              />
              <span className={styles.mfaHint}>
                Enter the code from your authenticator app, or one of your backup codes.
              </span>
            </div>
          )}

          {error && (
            <div className={styles.errorAlert}>
              <span className={styles.errorIcon}>⚠️</span>
              {error}
            </div>
          )}

          {verificationMessage && !error && (
            <div className={styles.successAlert}>
              <span className={styles.successIcon}>✓</span>
              {verificationMessage}
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className={styles.loadingSpinner}>Loading...</span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.signupPrompt}>
            Don't have an account?{' '}
            <Link to="/register" state={{ from: location.state?.from }} className={styles.signupLink}>
              Sign up
            </Link>
          </p>
        </div>

        <div className={styles.divider}>
          <span className={styles.dividerText}>or</span>
        </div>

        <div className={styles.socialLogin}>
          <button
            className={styles.socialButton}
            disabled={isLoading || googleLoading}
            onClick={handleGoogleLogin}
            type="button"
          >
            <svg className={styles.googleIcon} viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>
          <button className={styles.socialButton} disabled={isLoading} type="button">
            <span className={styles.socialIcon}>🔗</span>
            Continue with GitHub
          </button>
        </div>
      </div>
    </div>
  );
}
